import React from "react";
import { Link } from "react-router-dom";
import FIcon from "../FIcon";
import { FSidebarItem } from "./types";
import { isLinkActive } from "./utils";

interface NavigationLinkProps {
  item: FSidebarItem;
  currentPath: string;
  isMobile?: boolean;
  collapsed?: boolean;
  onClose?: () => void;
  onClick?: (e: React.MouseEvent) => void;
}

const NavigationLink: React.FC<NavigationLinkProps> = ({
  item,
  currentPath,
  isMobile = false,
  collapsed = false,
  onClose,
  onClick,
}) => {
  const { icon, name, href, target = "_self", upgrade_required_label } = item;

  const isActive = isLinkActive(href, currentPath);

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick(e);
    }

    if (item.onClick) {
      e.preventDefault();
      item.onClick(item);
      return;
    }

    if (href && onClose) {
      if (href.startsWith("http")) {
        // External link
        window.open(href, target);
        e.preventDefault();
      } else {
        onClose();
      }
    }
  };

  // Base classes for mobile and desktop
  const baseClasses = isMobile
    ? `flex items-center gap-2 px-4 py-3 hover:bg-white ${
        isActive
          ? "!bg-white text-dark_night_sky font-medium shadow-sm rounded-lg"
          : "text-neutral_700"
      }`
    : `cursor-pointer flex flex-row items-center h-[40px] text-[14px] font-normal no-underline rounded-[8px] ${
        collapsed ? "pl-[17px]" : "pl-[12px]"
      } justify-between ${
        isActive
          ? collapsed
            ? "text-dark_night_sky text-bold"
            : "text-white text-bold"
          : "text-dark_night_sky"
      }`;

  // Disabled styles
  const disabledClasses = upgrade_required_label
    ? "appcue-upgrade pointer-events-none"
    : "";

  // Combined classes
  const combinedClasses = `${baseClasses} ${disabledClasses}`;

  // Icon with appropriate styles based on active state and environment (mobile/desktop)
  const iconElement = icon && (
    <div className={isMobile ? "w-6 flex justify-center" : ""}>
      <FIcon
        icon={icon}
        size={isMobile ? 15 : 18}
        color={
          isMobile
            ? isActive
              ? "#000F45"
              : "#5C6276"
            : isActive
              ? collapsed
                ? "#000F45"
                : "#FFFFFF"
              : "#FFFFFF"
        }
        className={isMobile ? "-mt-[2px]" : ""}
      />
    </div>
  );

  // Content to render for the link
  const content = (
    <>
      {iconElement}
      <span>{name}</span>
      {upgrade_required_label && (
        <span className="ml-auto text-[15px] bg-highlight_red text-dark_night_sky px-2 py-1 rounded">
          {upgrade_required_label}
        </span>
      )}
    </>
  );

  return href ? (
    <Link
      to={href}
      className={combinedClasses}
      target={target}
      onClick={handleClick}
    >
      {content}
    </Link>
  ) : (
    <div className={baseClasses} onClick={handleClick}>
      {content}
    </div>
  );
};

export default NavigationLink;
